/**
 * The chat cards every Workspace widget shows, in its List, Tiles or Card view
 * (design: Chat Collection.dc.html). Widgets build `CardItem`s; this only draws.
 */
import { memo, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../components/FeatherIcon';
import { LoadingBlock } from '../../components/Spinner';
import type { WidgetView } from '../board';
import { Avatar, FanAvatar } from './Avatar';

export interface CardKind {
  label: string;
  icon: string;
  /** oklch hue of the kind's meaning colour. */
  hue: number;
  amount?: string;
}

export interface CardItem {
  key: string;
  accountId: string;
  fanId: string;
  fanName: string;
  fanUsername?: string | null;
  fanAvatar?: string | null;
  modelName: string;
  modelAvatar?: string | null;
  modelHandle?: string;
  accent: string;
  tint: string;
  bp: number | null;
  spent?: string | null;
  sub: string;
  subIcon: string;
  subTone?: 'muted' | 'strong' | 'accent';
  time: string;
  flag?: string | null;
  kind?: CardKind | null;
  online?: boolean;
  dim?: number;
  group?: { label: string; accent?: boolean } | null;
  action?: { icon: string; tip: string; run: () => void } | null;
  stats?: { label: string; value: string }[];
}

interface Props {
  items: CardItem[];
  view: WidgetView;
  selectedKey: string | null;
  cardIndex: number;
  onCardIndex: (delta: number) => void;
  onOpen: (item: CardItem) => void;
  /** Pointer pressed on a card: the page's chat drag (useChatDrag) takes it from here. */
  onDragStart: (item: CardItem, e: PointerEvent) => void;
  /** The card being dragged right now (dimmed). */
  draggingKey?: string | null;
  /** `loading` shows the console's spinner instead of the empty text. */
  empty: { icon: string; title: string; text: string; action?: ReactNode; loading?: boolean };
  /** Pinned's drop zone, shown after the items. */
  drop?: { text: string; over: boolean } | null;
  /** Hover/keyboard highlight (J/K). */
  focusKey?: string | null;
}

function Dots({ value, size }: { value: number | null; size: number }) {
  return (
    <span className="ws-dots">
      {[0, 1, 2, 3, 4].map((k) => (
        <span key={k} style={{ width: size, height: size }} className={value != null && k < value ? 'is-on' : ''} />
      ))}
    </span>
  );
}

function kindStyle(k: CardKind) {
  return { color: `oklch(0.8 0.1 ${k.hue})`, background: `oklch(0.8 0.1 ${k.hue} / 0.14)` };
}

function ActionButton({ item, size }: { item: CardItem; size: number }) {
  if (!item.action) return null;
  const { run, tip, icon } = item.action;
  return (
    <button
      type="button"
      className="ws-card-act"
      title={tip}
      style={{ width: size, height: size }}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        run();
      }}
    >
      <FeatherIcon name={icon} size={13} />
    </button>
  );
}

/** Flag, time and the card's action, pushed to the right of a row's first line. */
function RowMeta({ item }: { item: CardItem }) {
  return (
    <>
      <span className="ws-flex" />
      {item.flag && <span className="ws-flag">{item.flag}</span>}
      <span className="ws-time">{item.time}</span>
      <ActionButton item={item} size={20} />
    </>
  );
}

/**
 * Same props → same cards. `empty` and `drop` are compared by content (pages
 * build them inline); callbacks must be stable (the page passes stable ones).
 */
function sameProps(a: Props, b: Props): boolean {
  const keys = Object.keys(b) as (keyof Props)[];
  if (keys.length !== Object.keys(a).length) return false;
  for (const k of keys) {
    if (k === 'empty') {
      const x = a.empty;
      const y = b.empty;
      if (x.icon !== y.icon || x.title !== y.title || x.text !== y.text || !!x.loading !== !!y.loading || !!x.action !== !!y.action) return false;
    } else if (k === 'drop') {
      const x = a.drop;
      const y = b.drop;
      if (!x !== !y || (x && y && (x.text !== y.text || x.over !== y.over))) return false;
    } else if (a[k] !== b[k]) return false;
  }
  return true;
}

export const ChatCollection = memo(function ChatCollection({ items, view, selectedKey, cardIndex, onCardIndex, onOpen, onDragStart, draggingKey, empty, drop, focusKey }: Props) {
  const dragProps = (item: CardItem) => ({ 'data-chat-key': item.key, onPointerDown: (e: PointerEvent) => onDragStart(item, e) });
  const { t } = useTranslation('workspace');
  const n = items.length;
  const dropZone = drop ? (
    <div className={`ws-drop ${drop.over ? 'is-over' : ''}`}>
      <FeatherIcon name="grip-vertical" size={14} />
      {drop.text}
    </div>
  ) : null;

  if (!n && empty.loading) {
    return (
      <div className="ws-empty">
        <LoadingBlock pad="0" label={empty.title} />
      </div>
    );
  }

  if (!n && !(drop && view !== 'card')) {
    return (
      <div className="ws-empty">
        <FeatherIcon name={empty.icon} size={22} />
        <b>{empty.title}</b>
        <span>{empty.text}</span>
        {empty.action}
      </div>
    );
  }

  if (view === 'card' && n) {
    const i = ((cardIndex % n) + n) % n;
    const cur = items[i]!;
    const upNext = n > 1 ? [1, 2, 3].slice(0, Math.min(3, n - 1)).map((k) => items[(i + k) % n]!) : [];
    return (
      <div className="ws-cardview ws-scroll">
        <div
          className={`ws-bigcard ${selectedKey === cur.key ? 'is-selected' : ''} ${draggingKey === cur.key ? 'is-dragging' : ''}`}
          {...dragProps(cur)}
          onClick={() => onOpen(cur)}
        >
          <div className="ws-bigcard-head" style={{ background: cur.tint }}>
            <Avatar src={cur.modelAvatar} name={cur.modelName} size={20} ring={`0 0 0 1.5px ${cur.accent}`} />
            <span className="ws-bigcard-model" style={{ color: cur.accent }}>
              {cur.modelName}
            </span>
            {cur.modelHandle && <span className="ws-bigcard-handle">{cur.modelHandle}</span>}
            <span className="ws-flex" />
            {cur.online && (
              <span className="ws-online-label">
                <span />
                {t('card.online')}
              </span>
            )}
            {cur.flag && <span className="ws-flag">{cur.flag}</span>}
            <span className="ws-bigcard-pos">
              {i + 1} / {n}
            </span>
          </div>
          <div className="ws-bigcard-body">
            <FanAvatar accountId={cur.accountId} src={cur.fanAvatar} name={cur.fanName} size={52} />
            <span className="ws-bigcard-fan">{cur.fanName}</span>
            {cur.fanUsername && <span className="ws-bigcard-user">@{cur.fanUsername}</span>}
            <span className="ws-bigcard-stats">
              {cur.spent && <b>{cur.spent}</b>}
              <Dots value={cur.bp} size={6} />
            </span>
          </div>
          {cur.kind && (
            <div className="ws-bigcard-kind">
              <span className="ws-kind-ic ws-kind-ic--24" style={kindStyle(cur.kind)}>
                <FeatherIcon name={cur.kind.icon} size={13} />
              </span>
              <span style={{ color: kindStyle(cur.kind).color }} className="ws-kind-label">
                {cur.kind.label}
              </span>
              {cur.kind.amount && <b>{cur.kind.amount}</b>}
              <span className="ws-flex" />
              <span className="ws-time">{cur.time}</span>
            </div>
          )}
          {!!cur.stats?.length && (
            <div className="ws-bigcard-statgrid">
              {cur.stats.map((s) => (
                <span key={s.label}>
                  <i>{s.label}</i>
                  <b>{s.value}</b>
                </span>
              ))}
            </div>
          )}
          <div className="ws-bigcard-sub">
            <FeatherIcon name={cur.subIcon} size={13} />
            <span>{cur.sub}</span>
          </div>
        </div>
        <div className="ws-bigcard-nav">
          <span className="ws-bigcard-btns">
            <button type="button" className="ws-btn-sq" title={t('card.prev')} onClick={() => onCardIndex(-1)}>
              <FeatherIcon name="arrow-left" size={14} />
            </button>
            {cur.action && (
              <button type="button" className="ws-btn-sq" title={cur.action.tip} onClick={cur.action.run}>
                <FeatherIcon name={cur.action.icon} size={14} />
              </button>
            )}
            <button type="button" className="ws-btn-sq" title={t('card.next')} onClick={() => onCardIndex(1)}>
              <FeatherIcon name="arrow-right" size={14} />
            </button>
          </span>
          <button type="button" className="ws-btn-primary" onClick={() => onOpen(cur)}>
            {t('card.open')}
          </button>
        </div>
        {upNext.length > 0 && (
          <div className="ws-upnext">
            <span>{t('card.upNext')}</span>
            {upNext.map((u) => (
              <span key={u.key} className="ws-pill">
                <FanAvatar accountId={u.accountId} src={u.fanAvatar} name={u.fanName} size={16} ring={`0 0 0 1.5px ${u.accent}`} />
                {u.fanName}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (view === 'tiles') {
    return (
      <div className="ws-tiles ws-scroll">
        {items.map((p) => (
          <div
            key={p.key}
            className={`ws-tile ${selectedKey === p.key ? 'is-selected' : ''} ${focusKey === p.key ? 'is-focus' : ''} ${draggingKey === p.key ? 'is-dragging' : ''}`}
            style={{ opacity: p.dim ?? 1 }}
            {...dragProps(p)}
            onClick={() => onOpen(p)}
          >
            <span className="ws-tile-model">
              <Avatar src={p.modelAvatar} name={p.modelName} size={18} ring={`0 0 0 1.5px ${p.accent}`} />
              <span style={{ color: p.accent }}>{p.modelName}</span>
            </span>
            <span className="ws-tile-fan">
              <FanAvatar accountId={p.accountId} src={p.fanAvatar} name={p.fanName} size={18} />
              <span>{p.fanName}</span>
            </span>
            {p.kind && (
              <span className="ws-tile-kind" style={{ color: kindStyle(p.kind).color }}>
                <FeatherIcon name={p.kind.icon} size={12} />
                {p.kind.label}
                {p.kind.amount && <b>{p.kind.amount}</b>}
              </span>
            )}
            <span className={`ws-tile-sub ws-tone-${p.subTone || 'muted'}`}>{p.sub}</span>
            <span className="ws-tile-foot">
              <Dots value={p.bp} size={4} />
              {p.spent && <b>{p.spent}</b>}
              <span className="ws-flex" />
              <span className="ws-time">{p.time}</span>
            </span>
            <span className="ws-tile-corner">
              {p.flag && <span className="ws-flag">{p.flag}</span>}
              <ActionButton item={p} size={20} />
            </span>
          </div>
        ))}
        {dropZone}
      </div>
    );
  }

  return (
    <div className="ws-list ws-scroll">
      {items.map((r) => (
        <div key={r.key} className="ws-list-item">
          {r.group && <span className={`ws-group ${r.group.accent ? 'is-accent' : ''}`}>{r.group.label}</span>}
          <div
            className={`ws-row ${selectedKey === r.key ? 'is-selected' : ''} ${focusKey === r.key ? 'is-focus' : ''} ${draggingKey === r.key ? 'is-dragging' : ''}`}
            style={{ opacity: r.dim ?? 1 }}
            {...dragProps(r)}
            onClick={() => onOpen(r)}
          >
            <span className="ws-row-bar" style={{ background: r.accent }} />
            {r.kind ? (
              <span className="ws-kind-ic" style={kindStyle(r.kind)}>
                <FeatherIcon name={r.kind.icon} size={15} />
              </span>
            ) : (
              <FanAvatar accountId={r.accountId} src={r.fanAvatar} name={r.fanName} size={30} ring={`0 0 0 2px ${r.accent}`}>
                {r.online && <span className="ws-online-dot" title={t('card.online')} />}
              </FanAvatar>
            )}
            <span className="ws-row-main">
              {r.kind && (
                <span className="ws-row-kind">
                  <span style={{ color: kindStyle(r.kind).color }}>{r.kind.label}</span>
                  {r.kind.amount && <b>{r.kind.amount}</b>}
                  <RowMeta item={r} />
                </span>
              )}
              <span className="ws-row-line">
                <Avatar src={r.modelAvatar} name={r.modelName} size={16} ring={`0 0 0 1.5px ${r.accent}`} />
                <span className="ws-row-model" style={{ color: r.accent }}>
                  {r.modelName}
                </span>
                {!r.kind && <RowMeta item={r} />}
              </span>
              <span className="ws-row-line ws-row-line--fan">
                {r.kind && <FanAvatar accountId={r.accountId} src={r.fanAvatar} name={r.fanName} size={16} />}
                <span className="ws-row-fan">{r.fanName}</span>
                <Dots value={r.bp} size={4} />
                {r.spent && <span className="ws-row-spent">{r.spent}</span>}
              </span>
              <span className="ws-row-sub">
                <FeatherIcon name={r.subIcon} size={12} />
                <span className={`ws-tone-${r.subTone || 'muted'}`}>{r.sub}</span>
              </span>
            </span>
          </div>
        </div>
      ))}
      {dropZone}
    </div>
  );
}, sameProps);
